import React, { useState } from 'react';
import { Tooltip, Zoom, styled, tooltipClasses, ClickAwayListener } from '@mui/material';

/**
 * A premium, interactive tooltip that works perfectly on both desktop (hover)
 * and mobile (tap). It includes smooth scaling animations and custom styling.
 */
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
    color: theme.palette.text.primary,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    borderRadius: '12px',
    padding: '10px 16px',
    fontSize: '0.875rem',
    fontWeight: 600,
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(22, 51, 0, 0.08)'}`,
    maxWidth: 300,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
    '&:before': {
      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(22, 51, 0, 0.08)'}`,
    }
  },
}));

const InteractiveTooltip = ({ title, children, placement = 'top', arrow = true }) => {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div style={{ display: 'inline-block' }}>
        <StyledTooltip
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={title}
          placement={placement}
          arrow={arrow}
          TransitionComponent={Zoom}
          TransitionProps={{ timeout: 400 }}
          enterTouchDelay={0}
          leaveTouchDelay={5000}
        >
          <span 
            onClick={handleToggle} 
            onMouseEnter={handleTooltipOpen} 
            onMouseLeave={handleTooltipClose}
            onFocus={handleTooltipOpen}
            onBlur={handleTooltipClose}
            tabIndex={0}
            style={{ cursor: 'help', outline: 'none' }}
          >
            {children}
          </span>
        </StyledTooltip>
      </div>
    </ClickAwayListener>
  );
};

export default InteractiveTooltip;
